/* eslint-disable no-shadow */
/* eslint-disable prefer-destructuring */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable consistent-return */
/* eslint-disable max-classes-per-file */
/* eslint-disable class-methods-use-this */

import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { PDFManagerFactory, WebViewerWrapper } from 'pdftron';
import { getIsSingleFile, getLastJobInput } from 'store';
import store from 'store/store';
import { InspectedAnnotationCustomData, PDFTronToolsDBVals, DocumentTypes } from 'types';
import { isRectangularCrop, isPolygonCrop } from './AnnotationTools/utils/CropToolUtils';
import AnnotationMixin from './Annotation';
import GVAnnotationMixin from './AnnotationTools/utils/GVAnnotationMixin';
import quadsToDimensions from './utils/quadsToDimensions';
import {
  getPageRotation,
  calculateRotatedLabelPosition,
  drawCounterRotatedLabel,
} from './AnnotationTools/utils/labelPositionUtils';

/**
  InspectedAnnotation is used to display the user-created annotations (Text|Marquee|Crossout)
  in the results tab for the CURRENT inspection by lastJobInput
*/
class InspectedAnnotation {
  source: WebViewerWrapper | null = null;

  target: WebViewerWrapper | null = null;

  constructor(source: WebViewerWrapper | null, target: WebViewerWrapper | null) {
    this.source = source;
    this.target = target;
  }

  get annotationMixin() {
    return new AnnotationMixin(this.source, this.target);
  }

  /**
    generate a new list of inspected annotations for the last inspection
   */
  generateInspectedAnnotations(documentType: DocumentTypes) {
    const instance = this[documentType]?.instance;
    if (!instance) {
      return null;
    }

    const lastJobInput = getLastJobInput(store.getState());
    // must have existing inspection job to generate inspected annotations
    if (!lastJobInput) {
      return null;
    }

    // should delete any existing inspectedAnnotations before generating new list of inspectedAnnotations
    this.deleteExistingInspectedAnnotations(documentType);

    const documentTypes = {
      [DocumentTypes.source]: 'master',
      [DocumentTypes.target]: 'sample',
    };

    lastJobInput.documents?.forEach((inputDocument) => {
      if (documentTypes[documentType] === inputDocument.type) {
        const inspectedAnnotationsList: Core.Annotations.Annotation[] = [];

        const InspectedPolygon = this.InspectedPolygonClass(instance);
        if (inputDocument.polygons) {
          const pagePoints = inputDocument.polygons.points;
          const documentWrapper = PDFManagerFactory.getViewer(documentType);
          if (!documentWrapper) return;
          const instanceSource = documentWrapper.instance;
          const manager = PDFManagerFactory.getPDFDocManager();
          if (!manager || !instanceSource) return;

          for (let i = 0; i < pagePoints.length; i++) {
            const polygonShapes = pagePoints[i];

            for (let j = 0; j < polygonShapes.length; j++) {
              const points = polygonShapes[j];

              const annotation = new InspectedPolygon(inputDocument.polygons.layerOrSeparationName as string);

              for (let k = 0; k < points.length; k++) {
                annotation.addPathPoint(Number(points[k][0]), Number(points[k][1]));
              }

              inspectedAnnotationsList.push(annotation);
            }
          }
        }

        const InspectedAnnotation = this.InspectedAnnotationClass(instance);
        inputDocument.pages?.forEach((inputPage) => {
          // convert crossouts to inspectedAnnotations
          inputPage.crossout?.forEach(({ number, quads }) => {
            const dimensions = quadsToDimensions(quads);
            const tagText = `Ignore Text ${number}`;

            const inspectedCrossoutAnnot = new InspectedAnnotation(tagText);
            inspectedCrossoutAnnot.X = dimensions.X;
            inspectedCrossoutAnnot.Y = dimensions.Y;
            inspectedCrossoutAnnot.Width = dimensions.Width;
            inspectedCrossoutAnnot.Height = dimensions.Height;
            inspectedCrossoutAnnot.PageNumber = inputPage.number;

            inspectedAnnotationsList.push(inspectedCrossoutAnnot);
          });

          // convert text/marquee zones to inspectedAnnotations
          inputPage.zone?.forEach((zone) => {
            const dimensions = quadsToDimensions(zone.quads);
            const tagText = `${zone.usedTool === PDFTronToolsDBVals.MARQUEE_ZONE ? 'Marquee Zone' : 'Text Select'} ${
              zone.number
            }`;

            const inspectedZoneAnnot = new InspectedAnnotation(tagText);
            inspectedZoneAnnot.X = dimensions.X;
            inspectedZoneAnnot.Y = dimensions.Y;
            inspectedZoneAnnot.Width = dimensions.Width;
            inspectedZoneAnnot.Height = dimensions.Height;
            inspectedZoneAnnot.PageNumber = inputPage.number;
            // keep hidden when first creating inspected annotations
            inspectedZoneAnnot.Hidden = true;

            inspectedAnnotationsList.push(inspectedZoneAnnot);
          });

          // convert crop zones to inspectedAnnotations/InspectedPolygon
          inputPage.crop?.forEach((crop) => {
            if (isRectangularCrop(crop)) {
              const tagText = `Crop Zone`;

              const inspectedCropAnnot = new InspectedAnnotation(tagText);
              inspectedCropAnnot.X = crop.x1;
              inspectedCropAnnot.Y = crop.y1;
              inspectedCropAnnot.Width = crop.x2 - crop.x1;
              inspectedCropAnnot.Height = crop.y2 - crop.y1;
              inspectedCropAnnot.PageNumber = inputPage.number;

              // Store cropId so we can identify if it's an unmatchedCrop
              if (crop.cropId) {
                inspectedCropAnnot.setCustomData('cropId', crop.cropId);
              }

              // keep hidden when first creating inspected annotations
              inspectedCropAnnot.Hidden = true;

              inspectedAnnotationsList.push(inspectedCropAnnot);
            } else if (isPolygonCrop(crop)) {
              const tagText = `Crop Zone`;
              const inspectedPolygonCropAnnot = new InspectedPolygon(tagText);
              if (crop.points) {
                for (const [x, y] of crop.points) {
                  inspectedPolygonCropAnnot.addPathPoint(Number(x), Number(y));
                }
              }

              inspectedPolygonCropAnnot.PageNumber = inputPage.number;

              // Store cropId so we can identify if it's an unmatchedCrop
              if (crop.cropId) {
                inspectedPolygonCropAnnot.setCustomData('cropId', crop.cropId);
              }

              // keep hidden when first creating inspected annotations
              inspectedPolygonCropAnnot.Hidden = true;

              inspectedAnnotationsList.push(inspectedPolygonCropAnnot);
            }
          });
        });

        // convert manual align zones to inspectedAnnotations (rectangular, same as crop zones)
        inputDocument.manualAlignZones?.forEach((zone) => {
          const tagText = `Manual Align Zone`;

          const inspectedManualAlignAnnot = new InspectedAnnotation(tagText);
          inspectedManualAlignAnnot.X = zone.x1;
          inspectedManualAlignAnnot.Y = zone.y1;
          inspectedManualAlignAnnot.Width = zone.x2 - zone.x1;
          inspectedManualAlignAnnot.Height = zone.y2 - zone.y1;
          inspectedManualAlignAnnot.PageNumber = zone.pageNumber;

          inspectedManualAlignAnnot.Hidden = true;

          inspectedAnnotationsList.push(inspectedManualAlignAnnot);
        });

        // add and draw the annotations
        instance.Core.annotationManager.addAnnotation(inspectedAnnotationsList);
      }
    });
  }

  changeInspectedAnnotationVisibility(visible: boolean) {
    const isSingleFile = getIsSingleFile(store.getState());

    if ((isSingleFile && !this.target) || (!isSingleFile && (!this.source || !this.target))) null;

    let sourceInspectedAnnotations;
    if (this.source) {
      sourceInspectedAnnotations = this.source.annotationManager
        .getAnnotationsList()
        .filter(
          (annot: Core.Annotations.Annotation) =>
            annot.getCustomData(InspectedAnnotationCustomData.inspectedAnnotation) === 'true',
        );
    }
    let targetInspectedAnnotations;
    if (this.target) {
      targetInspectedAnnotations = this.target.annotationManager
        .getAnnotationsList()
        .filter(
          (annot: Core.Annotations.Annotation) =>
            annot.getCustomData(InspectedAnnotationCustomData.inspectedAnnotation) === 'true',
        );
    }

    if (visible) {
      sourceInspectedAnnotations && this.source?.annotationManager.showAnnotations(sourceInspectedAnnotations);
      targetInspectedAnnotations && this.target?.annotationManager.showAnnotations(targetInspectedAnnotations);
    } else {
      sourceInspectedAnnotations && this.source?.annotationManager.hideAnnotations(sourceInspectedAnnotations);
      targetInspectedAnnotations && this.target?.annotationManager.hideAnnotations(targetInspectedAnnotations);
    }
  }

  deleteExistingInspectedAnnotations(documentType: DocumentTypes) {
    if (!this[documentType]) {
      return null;
    }

    this[documentType]!.deleteAllAnnotationsByFilter(
      (annot) => annot.getCustomData(InspectedAnnotationCustomData.inspectedAnnotation) === 'true',
    );
  }

  isUnmatchedCrop(cropId: string): boolean {
    const unmatchedCropIds = store.getState().inspection.lastJobUnmatchedCropIds;
    if (!unmatchedCropIds || !Array.isArray(unmatchedCropIds)) {
      return false;
    }

    return unmatchedCropIds.includes(cropId);
  }

  // get inspectedAnnotation class
  InspectedAnnotationClass(instance: WebViewerInstance) {
    const outerThis = this;
    const isTargetDoc = instance === this.target?.instance;

    class InspectedAnnotation extends GVAnnotationMixin(instance.Core.Annotations.MarkupAnnotation) {
      private label: string;

      constructor(label: string) {
        super();

        this.setCustomData(InspectedAnnotationCustomData.inspectedAnnotation, 'true');
        this.Subject = 'InspectedAnnotation';
        this.ReadOnly = true;
        this.label = label;
        this.IsHoverable = true;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      draw(ctx: CanvasRenderingContext2D, _pageMatrix: any, pageRotation: number, _zoomLevel: number) {
        const realPageRotation = getPageRotation(instance, this.PageNumber);
        // Check if this crop is unmatched (but never show orange on target doc)
        const cropId = this.getCustomData('cropId');
        const isUnmatched = cropId && !isTargetDoc ? outerThis.isUnmatchedCrop(cropId) : false;

        // Use orange for unmatched crops, light blue for matched/normal
        ctx.strokeStyle = isUnmatched ? 'rgba(255, 152, 0, 0.9)' : 'rgba(109, 218, 226, 0.9)';
        ctx.setLineDash([5, 3]);
        const cropBorderWidth = 1;
        ctx.lineWidth = cropBorderWidth;
        ctx.strokeRect(this.X, this.Y, this.Width, this.Height);

        const annotationRectangle = new Path2D();
        annotationRectangle.rect(this.X, this.Y, this.Width, this.Height);
        ctx.fillStyle = 'rgba(0,0,0,0.01)'; // requires a minimum opacity to have the inner rectangle hoverable
        ctx.fill(annotationRectangle);

        if ((this as any)?.IsHovering) {
          // This forces the annotation / label to be infront of everything else.
          instance.Core.annotationManager.bringToBack(this);

          // Update label text if this is an unmatched crop
          const displayLabel = isUnmatched ? 'Crop Zone: Match not found' : this.label;

          const labelFont = '11px Inter, sans-serif';
          ctx.font = labelFont;
          const { width } = ctx.measureText(displayLabel);
          const labelWidth = width + 10;
          const labelHeight = 16;
          const labelDimensions = { width: labelWidth, height: labelHeight };

          // Calculate label position based on page rotation to keep it in perceived top-left
          const bounds = { x: this.X, y: this.Y, width: this.Width, height: this.Height };
          const pgW = instance.Core.documentViewer.getPageWidth(this.PageNumber);
          const pgH = instance.Core.documentViewer.getPageHeight(this.PageNumber);
          const labelPosition = calculateRotatedLabelPosition(
            bounds,
            labelDimensions,
            realPageRotation,
            0,
            false,
            pgW,
            pgH,
          );

          // Use orange background for unmatched, light blue for matched
          const fillStyle = isUnmatched ? 'rgba(255, 152, 0, 0.9)' : 'rgba(109, 218, 226, 0.9)';

          // Draw the label with counter-rotation so it always appears horizontal
          drawCounterRotatedLabel(
            ctx,
            displayLabel,
            labelPosition.x,
            labelPosition.y,
            labelWidth,
            labelHeight,
            realPageRotation,
            fillStyle,
            5, // textPadding
            2, // borderRadius
            undefined, // no icon
            12, // iconSize
            2, // iconPadding
            labelFont,
            cropBorderWidth,
          );
        }
      }
    }
    return InspectedAnnotation;
  }

  InspectedPolygonClass(instance: WebViewerInstance) {
    const outerThis = this;
    const isTargetDoc = instance === this.target?.instance;

    class InspectedPolygon extends GVAnnotationMixin(instance.Core.Annotations.PolygonAnnotation) {
      private label: string;

      constructor(label: string) {
        super();
        this.setCustomData(InspectedAnnotationCustomData.inspectedAnnotation, 'true');
        this.Subject = 'InspectedAnnotation';
        this.ReadOnly = true;
        this.label = label;
        this.IsHoverable = true;
      }

      get annotationPoints() {
        return this.getPath();
      }

      draw(ctx: CanvasRenderingContext2D, _pageMatrix: any, pageRotation: number, _zoomLevel: number) {
        const realPageRotation = getPageRotation(instance, this.PageNumber);
        const points = this.annotationPoints;

        // Check if this crop is unmatched (but never show orange on target doc)
        const cropId = this.getCustomData('cropId');
        const isUnmatched = cropId && !isTargetDoc ? outerThis.isUnmatchedCrop(cropId) : false;

        // Use orange for unmatched crops, light blue for matched/normal
        ctx.strokeStyle = isUnmatched ? 'rgba(255, 152, 0, 0.9)' : 'rgba(109, 218, 226, 0.9)';
        ctx.setLineDash([5, 3]);
        const cropBorderWidth = 1;
        ctx.lineWidth = cropBorderWidth;
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
          ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.closePath();
        ctx.stroke();

        ctx.fillStyle = 'rgba(0,0,0,0.01)'; // Requires a minimum opacity to be hoverable
        ctx.fill();

        if ((this as any)?.IsHovering) {
          // Bring the annotation to the front when hovered
          instance.Core.annotationManager.bringToBack(this);

          // Update label text if this is an unmatched crop
          const displayLabel = isUnmatched ? 'Crop Zone: Match not found' : this.label;

          const labelFont = '11px Inter, sans-serif';
          ctx.font = labelFont;
          const { width } = ctx.measureText(displayLabel);
          const labelWidth = width + 10;
          const labelHeight = 16;
          const labelDimensions = { width: labelWidth, height: labelHeight };

          // Calculate label position based on page rotation to keep it in perceived top-left
          // Use first point of polygon as reference for position
          const bounds = { x: points[0].x, y: points[0].y, width: this.Width, height: this.Height };
          const pgW = instance.Core.documentViewer.getPageWidth(this.PageNumber);
          const pgH = instance.Core.documentViewer.getPageHeight(this.PageNumber);
          const labelPosition = calculateRotatedLabelPosition(
            bounds,
            labelDimensions,
            realPageRotation,
            0,
            false,
            pgW,
            pgH,
          );

          // Use orange background for unmatched, light blue for matched
          const fillStyle = isUnmatched ? 'rgba(255, 152, 0, 0.9)' : 'rgba(109, 218, 226, 0.9)';

          // Draw the label with counter-rotation so it always appears horizontal
          drawCounterRotatedLabel(
            ctx,
            displayLabel,
            labelPosition.x,
            labelPosition.y,
            labelWidth,
            labelHeight,
            realPageRotation,
            fillStyle,
            5, // textPadding
            2, // borderRadius
            undefined, // no icon
            12, // iconSize
            2, // iconPadding
            labelFont,
            cropBorderWidth,
          );
        }
      }
    }
    return InspectedPolygon;
  }
}

export default InspectedAnnotation;
